// @ts-strict-ignore
import { Injectable, NgZone, ViewContainerRef } from '@angular/core';
import { LMSubFormDialogComponent } from './lm-sub-form-dialog.component';
import { AppConstants } from '@utils/app.constants';
import { Observable, Subject } from 'rxjs';
import { LMModel } from '@form/model';
import { LMCommonUtilService, LMUniqueIdService } from '@utils/index';
import {
  LMBusyIndicatorService,
  LMFormatUtilService,
  LMModalDialogWrapperRef,
  LMModalWrapperService
} from '@services/index';
import { LMDialogButtonKey } from '@utils/app.constants';
import { LMFormRequestService, LMFormSpecUtilService } from '@form/services';

export enum LMSubFormDialogResult {
  RESPONSE_CANCEL = 0,
  RESPONSE_OK,
  RESPONSE_ERROR
}

export interface LMSubFormResult {
  result: LMSubFormDialogResult;
  dataView?: LMDataView;
  model?: LMModel;
  bindIds?: Array<string>;
  error?: LMSubFormError;
}

export interface LMSubFormError {
  message?: string;
  details?: string;
  status?: string;
  statusText?: string;
}

export interface LMSubFormDialogOptions {
  placeholder: ViewContainerRef;
  busFormSpec: LMBusFormSpec;
  title: LMMessageRule;
  dataView: LMDataView;
  formUrlInfo: LMFormUrlInfo;
  formSpec: LMFormSpec;
  model?: LMModel;
  // used by subform with an action
  actionInfo?: LMSubFormActionInfo
}

export interface LMSubFormActionInfo {
  actionSpec: LMActionSpec;
  actionSubject$: Subject<LMSubFormActionResults>;
  actionCallback: (result: LMSubFormResult, actionSpec: LMActionSpec, rows: Array<number>) => void;
  rows: Array<number>;
}

export interface LMSubFormActionResults {
  okToClose: boolean;
  actionSpec: LMActionSpec;
  unprocessedRows: Array<number>;
  result: LMActionResult;
}

@Injectable()
export class LMSubFormDialogService {
  constructor(
    private lmCommonUtilService: LMCommonUtilService,
    private lmBusyIndicatorService: LMBusyIndicatorService,
    private lmFormatUtilService: LMFormatUtilService,
    private lmUniqueIdService: LMUniqueIdService,
    private lmFormRequestService: LMFormRequestService,
    private lmFormSpecUtilService: LMFormSpecUtilService,
    private lmModalWrapperService: LMModalWrapperService
  ) {}

  public promptSubFormDialog(args: LMSubFormDialogOptions): Observable<LMSubFormResult> {
    const formSpec: LMFormSpec = this.buildSubFormDlgParams(args);
    const specFields: LMSpecFields = formSpec.busFormSpec.fields;
    const title = this.lmFormatUtilService.formatMessageRule(args.title, args.dataView, specFields);
    return this.openDialog(args.formUrlInfo, formSpec, args.dataView, args.placeholder, title, args.model, args.actionInfo);
  }

  private buildSubFormDlgParams(args: LMSubFormDialogOptions): LMFormSpec {
    // make a deep clone of the formSpec
    const formSpec: LMFormSpec = JSON.parse(JSON.stringify(args.formSpec));

    // replace the busFormSpec with a deep clone of the args.busFormSpec
    formSpec.busFormSpec = JSON.parse(JSON.stringify(args.busFormSpec));

    // update with the correct subFormType
    formSpec.busFormSpec.subFormType = AppConstants.BUTTON_TYPE_SUBFORM;

    // keep the original namePrefix, name, and current namePrefix
    formSpec.busFormSpec.namePrefix = this.lmUniqueIdService.uniqueId([ args.formSpec.busFormSpec.namePrefix, args.formSpec.busFormSpec.name, formSpec.busFormSpec.namePrefix ]);

    return formSpec;
  }

  private openDialog(
    formUrlInfo: LMFormUrlInfo,
    formSpec: LMFormSpec,
    dataView: LMDataView,
    placeholder: ViewContainerRef,
    title: string,
    parentModel?: LMModel,
    actionInfo?: LMSubFormActionInfo
  ): Observable<LMSubFormResult> {
    this.lmBusyIndicatorService.showBusyIndicator();

    return new Observable<LMSubFormResult>(observer => {

      if (!this.lmFormSpecUtilService.isStandardForm(formSpec)) {
        this.lmBusyIndicatorService.hideBusyIndicator();

        const formType = formSpec.busFormSpec.formType;
        const error: LMSubFormError = {
          message: this.lmFormatUtilService.formatMessage(Soho.Locale.translate('InvalidSubFormType'), formType),
          status: AppConstants.VIEW_EXCEPTION
        };
        const result: LMSubFormResult = {
          result: LMSubFormDialogResult.RESPONSE_ERROR,
          error
        };
        observer.next(result);
        observer.complete();
        return;
      }

      let dlgComponent: LMSubFormDialogComponent;
      let dialog: LMModalDialogWrapperRef<LMSubFormDialogComponent>;
      const bindIds = this.lmCommonUtilService.getBindIdsFromFormSpec(formSpec);
      const formNamePrefix = (formSpec.busFormSpec.namePrefix) ? formSpec.busFormSpec.namePrefix : '';
      const formName = formSpec.busFormSpec.name;
      let alertMessage: string;

      if (actionInfo?.rows.length > 1) {
        const count = actionInfo.rows.length;
        const phrase = actionInfo.actionSpec.type === AppConstants.ACTION_TYPE_DELETE ? 'DeleteConfirmationAlertMessage' : 'ActionConfirmationAlertMessage';
        alertMessage = this.lmFormatUtilService.formatMessage(Soho.Locale.translate(phrase), count);
      }

      const attributes: SohoAttributesArray = [ {
        name: 'data-automation-id',
        value: `lm-${formName}-modal`
      } ];

      // Build the buttons array
      const buttons = [
        {
          // TODO: The button default disables the button. A bug in controls?
          // tslint:disable-next-line - shadowed variable e
          id: this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.CANCEL ]),
          text: Soho.Locale.translate(LMDialogButtonKey.CANCEL),
          click: (e, modal) => {
            dialog.close(<LMSubFormResult> {
              result: LMSubFormDialogResult.RESPONSE_CANCEL
            });
          }
        },
        {
          id: this.lmUniqueIdService.uniqueId([ formNamePrefix, formName, LMDialogButtonKey.OK ]),
          text: Soho.Locale.translate(LMDialogButtonKey.OK),
          validate : false,
          isDefault: true,
          click: (e, modal) => {

            const result: LMSubFormResult = {
              result:   LMSubFormDialogResult.RESPONSE_OK,
              dataView: dlgComponent.formComponent.formModel.getData(),
              model:    dlgComponent.formComponent.formModel,
              bindIds
            };
            if (actionInfo) {
              // subform with actionSpec pattern
              dlgComponent.showBusyIndicator();
              actionInfo.actionCallback(result, actionInfo.actionSpec, actionInfo.rows);
              actionInfo.actionSubject$.subscribe(subFormActionResult => {
                dlgComponent.hideBusyIndicator();
                if (subFormActionResult.okToClose) {
                  dialog.close(result);
                } else {
                  actionInfo.rows = subFormActionResult.unprocessedRows;
                  dlgComponent.handleTransactionStatus(subFormActionResult.result);
                }
              });
            } else {
              dialog.close(result);
            }
          }
        }
      ];

      const defaultButton = buttons.find(button => button.isDefault);

      dialog = this.lmModalWrapperService
        .modal(LMSubFormDialogComponent, { fullsize: 'responsive', attributes })
        .buttons(buttons)
        .title(title)
        .isAlert(false)
        .beforeOpen(() => {
          this.lmBusyIndicatorService.hideBusyIndicator();
          return true;
        })
        .open();

      dialog.apply((subForm: LMSubFormDialogComponent) => {
        dlgComponent = subForm;

        subForm.formOptions = {};
        subForm.formUrlInfo = formUrlInfo;
        subForm.formSpec = formSpec;
        subForm.dataView = dataView;
        subForm.parentModel = parentModel;
        subForm.alertMessage = alertMessage;
        subForm.defaultButton = defaultButton;
      });

      dialog.afterClose(result => {
        observer.next(result);
        observer.complete();
      });
    });
  }

  public getBlankSubFormDataView(url: string, actionSpec: LMActionSpec): Observable<LMDataView> {

    return new Observable<LMDataView>(observer => {

      this.lmFormRequestService.getBlankFormView(url, actionSpec.index).subscribe(dataView => {
        if (dataView?.fields) {
          observer.next(dataView);
          observer.complete();
        } else {
          observer.next(null);
          observer.complete();
        }
      })
    });
  };

  public updateDataView(fromDataView: LMDataView, toDataView: LMDataView) {

    Object.keys(toDataView.fields).forEach(fieldName => {
      if (fromDataView.fields.hasOwnProperty(fieldName)) {
        let fromField: LMField = fromDataView.fields[ fieldName ];
        let toField: LMField = toDataView.fields[ fieldName ];
        toField.value = fromField.value;
      }
    });

    return toDataView;
  }

  // private handleSubformWithActionInfo(actionInfo: LMSubFormActionInfo) {
  //   this.ngZone.run(args => this.lmBusyIndicatorService.showBusyIndicator());
  //   actionInfo.actionCallback(result, actionInfo.actionSpec, actionInfo.rows);
  //   actionInfo.actionSubject$.subscribe(subFormActionResult => {
  //     this.lmBusyIndicatorService.hideBusyIndicator();
  //     if (subFormActionResult.okToClose) {
  //       dialog.close(result);
  //     } else {
  //       actionInfo.rows = subFormActionResult.unprocessedRows;
  //       dlgComponent.handleTransactionStatus(subFormActionResult.result);
  //     }
  //   });
  // }
}
